/**
 * PageBuilder — Drag-and-drop, resizable widget layout engine.
 *
 * Uses react-grid-layout for the grid. In edit mode the grid is interactive
 * (draggable + resizable). In read mode it is static and transparent.
 *
 * Layout JSON is persisted per user per tab via the monday Storage API
 * through the AppStore's saveLayout action.
 */

import { useCallback, useMemo, useState } from 'react';
import { Responsive, WidthProvider, Layout } from 'react-grid-layout';
import { Button, Tooltip } from 'monday-ui-react-core';
import { Add, Settings, Undo, Bolt } from 'monday-ui-react-core/icons';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import { WidgetRenderer } from './WidgetRenderer';
import { widgetRegistry } from './WidgetRegistry';
import { GRID_COLS, GRID_ROW_HEIGHT, GRID_BREAKPOINTS } from '../core/utils/defaults';
import { useAppStore } from '../core/store/appStore';
import type { WidgetGridItem, TabLayout, AppTab, MondayContext } from '../core/types';

const ResponsiveGridLayout = WidthProvider(Responsive);

// -------------------------------------------------------
// Add Widget drawer (shown in edit mode)
// -------------------------------------------------------

interface AddWidgetDrawerProps {
  onAdd: (widgetId: string) => void;
  surface: 'custom_object' | 'dashboard';
}

function AddWidgetDrawer({ onAdd, surface }: AddWidgetDrawerProps) {
  const available = widgetRegistry.getWidgetsBySurface(surface);

  return (
    <div className="add-widget-drawer">
      <p className="add-widget-drawer__title">Add Widget</p>
      <div className="add-widget-drawer__grid">
        {available.map((def) => (
          <button
            key={def.id}
            className="add-widget-tile"
            onClick={() => onAdd(def.id)}
            title={def.displayName}
          >
            <span className="add-widget-tile__icon">{def.icon}</span>
            <span className="add-widget-tile__label">{def.displayName}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// -------------------------------------------------------
// PageBuilder toolbar
// -------------------------------------------------------

interface PageBuilderToolbarProps {
  isEditMode: boolean;
  onToggleEdit: () => void;
  onSaveSnapshot: () => void;
  onResetPage: () => void;
  onAddWidget: () => void;
}

function PageBuilderToolbar({
  isEditMode,
  onToggleEdit,
  onSaveSnapshot,
  onResetPage,
  onAddWidget,
}: PageBuilderToolbarProps) {
  if (!isEditMode) {
    return (
      <div className="page-builder-toolbar page-builder-toolbar--view">
        <Tooltip content="Customise layout">
          <Button
            kind={Button.kinds.TERTIARY}
            size={Button.sizes.SMALL}
            onClick={onToggleEdit}
            leftIcon={Settings}
            aria-label="Customise layout"
          >
            Customise
          </Button>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="page-builder-toolbar page-builder-toolbar--edit">
      <Button
        kind={Button.kinds.PRIMARY}
        size={Button.sizes.SMALL}
        onClick={onAddWidget}
        leftIcon={Add}
      >
        Add Widget
      </Button>
      <Button
        kind={Button.kinds.TERTIARY}
        size={Button.sizes.SMALL}
        onClick={onSaveSnapshot}
        leftIcon={Bolt}
      >
        Save Snapshot
      </Button>
      <Button
        kind={Button.kinds.TERTIARY}
        size={Button.sizes.SMALL}
        onClick={onResetPage}
        leftIcon={Undo}
        className="btn-destructive-hint"
      >
        Reset Page
      </Button>
      <Button
        kind={Button.kinds.SECONDARY}
        size={Button.sizes.SMALL}
        onClick={onToggleEdit}
      >
        Done
      </Button>
    </div>
  );
}

// -------------------------------------------------------
// Main PageBuilder component
// -------------------------------------------------------

export interface PageBuilderProps {
  tab: AppTab;
  layout: TabLayout;
  surface: 'custom_object' | 'dashboard';
  context: MondayContext;
  onLayoutChange: (updated: TabLayout) => void;
  onOpenConfig: (instanceId: string) => void;
}

export function PageBuilder({
  tab,
  layout,
  surface,
  context,
  onLayoutChange,
  onOpenConfig,
}: PageBuilderProps) {
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const { isEditMode, setEditMode, saveSnapshot, resetPageLayout } = useAppStore();

  // Build the react-grid-layout Layout array from our WidgetGridItem array
  const gridLayouts = useMemo<{ [breakpoint: string]: Layout[] }>(() => {
    const items: Layout[] = layout.widgets.map((w) => ({
      i: w.instanceId,
      x: w.x,
      y: w.y,
      w: w.w,
      h: w.h,
      minW: w.minW ?? 2,
      minH: w.minH ?? 2,
      maxW: w.maxW,
      maxH: w.maxH,
    }));
    return { lg: items, md: items, sm: items };
  }, [layout.widgets]);

  const handleLayoutChange = useCallback(
    (_currentLayout: Layout[], allLayouts: { [key: string]: Layout[] }) => {
      const updatedLg = allLayouts['lg'] ?? allLayouts['md'] ?? _currentLayout;
      const updatedWidgets: WidgetGridItem[] = layout.widgets.map((w) => {
        const item = updatedLg.find((l) => l.i === w.instanceId);
        if (!item) return w;
        return { ...w, x: item.x, y: item.y, w: item.w, h: item.h };
      });
      onLayoutChange({ ...layout, widgets: updatedWidgets });
    },
    [layout, onLayoutChange]
  );

  const handleAddWidget = useCallback(
    (widgetId: string) => {
      const definition = widgetRegistry.getWidget(widgetId);
      if (!definition) return;

      const newItem: WidgetGridItem = {
        instanceId: `${widgetId}_${Date.now()}`,
        widgetId,
        x: 0,
        y: Infinity, // react-grid-layout will place it at the bottom
        w: definition.defaultSize?.w ?? 4,
        h: definition.defaultSize?.h ?? 3,
        minW: definition.minSize?.w ?? 2,
        minH: definition.minSize?.h ?? 2,
        maxW: definition.maxSize?.w,
        maxH: definition.maxSize?.h,
        config: { ...definition.defaultConfig },
      };

      onLayoutChange({ ...layout, widgets: [...layout.widgets, newItem] });
      setShowAddDrawer(false);
    },
    [layout, onLayoutChange]
  );

  const handleRemoveWidget = useCallback(
    (instanceId: string) => {
      const updated = layout.widgets.filter((w) => w.instanceId !== instanceId);
      onLayoutChange({ ...layout, widgets: updated });
    },
    [layout, onLayoutChange]
  );

  const handleSaveSnapshot = useCallback(() => {
    saveSnapshot(tab);
  }, [saveSnapshot, tab]);

  const handleResetPage = useCallback(() => {
    if (window.confirm('Reset this page to its default layout? This cannot be undone.')) {
      resetPageLayout(tab);
    }
  }, [resetPageLayout, tab]);

  return (
    <div className={`page-builder ${isEditMode ? 'page-builder--edit' : 'page-builder--view'}`}>
      <PageBuilderToolbar
        isEditMode={isEditMode}
        onToggleEdit={() => setEditMode(!isEditMode)}
        onSaveSnapshot={handleSaveSnapshot}
        onResetPage={handleResetPage}
        onAddWidget={() => setShowAddDrawer((s) => !s)}
      />

      {isEditMode && showAddDrawer && (
        <AddWidgetDrawer onAdd={handleAddWidget} surface={surface} />
      )}

      <ResponsiveGridLayout
        className="page-builder-grid"
        layouts={gridLayouts}
        breakpoints={GRID_BREAKPOINTS}
        cols={GRID_COLS}
        rowHeight={GRID_ROW_HEIGHT}
        isDraggable={isEditMode}
        isResizable={isEditMode}
        onLayoutChange={handleLayoutChange}
        margin={[12, 12]}
        containerPadding={[0, 0]}
        compactType="vertical"
        draggableHandle=".widget-drag-handle"
      >
        {layout.widgets.map((item) => (
          <div key={item.instanceId} className="page-builder-cell">
            {isEditMode && (
              <div className="widget-edit-overlay">
                <span className="widget-drag-handle" title="Drag to move">⠿</span>
                <button
                  className="widget-remove-btn"
                  onClick={() => handleRemoveWidget(item.instanceId)}
                  title="Remove widget"
                  aria-label="Remove widget"
                >
                  ×
                </button>
              </div>
            )}
            <WidgetRenderer
              widgetId={item.widgetId}
              instanceId={item.instanceId}
              instanceConfig={item.config}
              isEditMode={isEditMode}
              context={context}
              onOpenConfig={onOpenConfig}
            />
          </div>
        ))}
      </ResponsiveGridLayout>
    </div>
  );
}
